// Pixels-per-day por nivel de zoom. Día más generoso, trimestre más compacto.
// Semana y Día pintan una celda por día; Mes y Trimestre agrupan (ver escala.js).
export const ZOOM = {
  dia:       { px: 56,  label: 'Día' },
  semana:    { px: 24,  label: 'Semana' },
  mes:       { px: 7,   label: 'Mes' },
  trimestre: { px: 2.4, label: 'Trimestre' },
};

// De menos zoom a más zoom (el orden de los botones - / +).
export const ZOOM_ORDEN = ['trimestre', 'mes', 'semana', 'dia'];

// Margen (en días) que se deja antes y después del contenido al calcular la
// ventana visible, por zoom. Así la primera barra no queda pegada al borde.
export const MARGEN_VENTANA = { dia: 7, semana: 14, mes: 31, trimestre: 92 };

// Cuántos días renderizamos: 60 antes de hoy + 180 después = ~8 meses de panorama.
export const DAYS_BEFORE = 60;
export const DAYS_AFTER = 180;

export const ROW_HEIGHT = 36;
export const SIDEBAR_WIDTH = 280;

// ---- Posiciones en el lienzo del Gantt ----
//
// Todo lo que se ubica sobre la linea de tiempo usa la variable CSS --dw (ancho
// del dia en px) que la camara pone en el lienzo. Asi el zoom anima una sola
// variable y el navegador recoloca todo en cada cuadro, sin renders de React.
// `restaPx` descuenta un margen fijo (ej. el aire entre barras).
export const xDias = (dias) => `calc(var(--dw) * ${dias} * 1px)`;
export const anchoDias = (dias, restaPx = 0, minimoPx = 0) => {
  const base = restaPx ? `calc(var(--dw) * ${dias} * 1px - ${restaPx}px)` : xDias(dias);
  return minimoPx ? `max(${minimoPx}px, ${base})` : base;
};

// ---- Helpers de fechas ----

export const addDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

export const diffDays = (a, b) => Math.round((a.getTime() - b.getTime()) / 86400000);

export const isWeekend = (d) => {
  const dow = d.getDay();
  return dow === 0 || dow === 6;
};

export const dayOfWeekShort = (d) => {
  const labels = ['do', 'lu', 'ma', 'mi', 'ju', 'vi', 'sá'];
  return labels[d.getDay()];
};

// Lunes de la semana de la fecha (lunes-first).
export const startOfWeek = (d) => {
  const r = new Date(d);
  const day = r.getDay();
  const diff = (day + 6) % 7;
  r.setDate(r.getDate() - diff);
  return r;
};

// Número de semana ISO.
export const isoWeek = (d) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
};

// Rango cubierto por un set de tareas (fecha más temprana → más tardía).
// Devuelve null si ninguna tarea tiene fechas. Usado para pintar la barra
// agregada en la fila del header de un grupo (proyecto o categoría),
// estilo ClickUp.
export const rangeFromTareas = (tareas) => {
  let min = null;
  let max = null;
  (tareas || []).forEach((t) => {
    const fi = t.fecha_inicio ? new Date(t.fecha_inicio + 'T00:00:00') : null;
    const fv = t.fecha_vencimiento ? new Date(t.fecha_vencimiento + 'T00:00:00') : null;
    const start = fi || fv;
    const end = fv || fi;
    if (!start || !end) return;
    if (!min || start < min) min = start;
    if (!max || end > max) max = end;
  });
  return min && max ? { min, max } : null;
};

export const startOfDay = (d) => {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
};

export const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

export const startOfQuarter = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);

const parseISO = (s) => (s ? new Date(s + 'T00:00:00') : null);

// Ventana visible del Gantt calculada desde el contenido (tareas, hitos y
// fechas propias del proyecto), siempre incluyendo hoy. El inicio se alinea
// al comienzo de la unidad del zoom (lunes, mes o trimestre) para que el
// encabezado arranque limpio. Garantiza un ancho mínimo para llenar pantalla.
export const ventanaGantt = ({ tareas = [], hitos = [], extras = [], zoom, minWidthPx = 1600 }) => {
  const today = startOfDay(new Date());
  let min = today;
  let max = today;
  const incluir = (d) => {
    if (!d || Number.isNaN(d.getTime())) return;
    if (d < min) min = d;
    if (d > max) max = d;
  };
  tareas.forEach((t) => { incluir(parseISO(t.fecha_inicio)); incluir(parseISO(t.fecha_vencimiento)); });
  hitos.forEach((h) => incluir(parseISO(h.fecha)));
  extras.forEach((s) => incluir(typeof s === 'string' ? parseISO(s) : s));

  const px = ZOOM[zoom].px;
  const margen = MARGEN_VENTANA[zoom];
  let first = addDays(min, -margen);
  if (zoom === 'trimestre') first = startOfQuarter(first);
  else if (zoom === 'mes') first = startOfMonth(first);
  else first = startOfWeek(first);

  const minDias = Math.ceil(minWidthPx / px);
  const totalDays = Math.max(minDias, diffDays(addDays(max, margen), first) + 1);
  return {
    firstDay: first,
    totalDays,
    todayOffset: diffDays(today, first),
    contenido: { min, max },
  };
};
